<template>
  <div>
    <HeaderNavbar
      :title="$t('settings.candidate_fields.title')"
      :breadcrumbs="[
        { label: $t('nav.settings'), path: { name: 'settings' } },
        { label: $t('settings.candidate_fields.title'), path: { name: 'settings.candidate_fields' } },
      ]" />
    <div class="container-lead">
      <div class="mt-3 responsive-padding">
        <p class="text-muted">{{ $t('settings.candidate_fields.description') }}</p>

        <div class="card fields-card">
          <div class="card-body">
            <div v-if="store.isLoading && !store.hasLoaded" class="text-center text-muted py-4">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>

            <template v-else>
              <p class="text-muted" v-if="!store.sortedDefinitions.length">
                {{ $t('settings.candidate_fields.empty') }}
              </p>

              <ul class="field-list" v-else>
                <li v-for="field in store.sortedDefinitions" :key="field.field_key" class="field-row">
                  <span class="field-name text-truncate">{{ field.label }}</span>
                  <span class="badge field-type-badge">{{ $t(`candidate.fields.type_${field.type}`) }}</span>

                  <div class="field-actions">
                    <LeadButton
                      variant="icon"
                      :icon-start="IconPencil"
                      v-tooltip="$t('settings.candidate_fields.edit_title')"
                      @click="startEdit(field)" />
                    <LeadButton
                      variant="icon"
                      class="field-delete-btn"
                      :icon-start="IconTrash"
                      v-tooltip="$t('btn.delete')"
                      @click="promptDelete(field)" />
                  </div>
                </li>
              </ul>

              <div class="field-add">
                <LeadButton variant="white" :icon-start="IconPlus" @click="startCreate">
                  {{ $t('settings.candidate_fields.add_button') }}
                </LeadButton>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <CandidateFieldFormModal v-model="formModalShow" :definition="editingField" />

    <LiConfirmationDialog
      v-model:dialogShow="deleteDialog"
      :confirmButtonLabel="$t('btn.delete')"
      @onContinue="deleteConfirmed">
      <h1 class="mb-3">{{ $t('settings.candidate_fields.delete_title') }}</h1>
      <p>{{ $t('settings.candidate_fields.delete_text', { name: fieldToDelete?.label ?? '' }) }}</p>
    </LiConfirmationDialog>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { useCandidateFieldsStore } from '@/stores/candidateFields';
import { deleteCandidateField } from '@/api/candidateFields';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import CandidateFieldFormModal from '@/components/candidate/CandidateFieldFormModal.vue';
import { ConfirmationDialog as LiConfirmationDialog } from '@prospective/lithium';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-vue';
import type { CandidateFieldDefinition } from '@/types/candidate';

/* ---------------------------------- STATE --------------------------------- */
const router = useRouter();
const userStore = useUserStore();
const featureStore = useCustomerFeaturesStore();
const store = useCandidateFieldsStore();

const formModalShow = ref(false);
const editingField = ref<CandidateFieldDefinition | null>(null);
const deleteDialog = ref(false);
const fieldToDelete = ref<CandidateFieldDefinition | null>(null);

/* --------------------------------- METHODS -------------------------------- */
const startCreate = () => {
  editingField.value = null;
  formModalShow.value = true;
};

const startEdit = (field: CandidateFieldDefinition) => {
  editingField.value = field;
  formModalShow.value = true;
};

const promptDelete = (field: CandidateFieldDefinition) => {
  fieldToDelete.value = field;
  deleteDialog.value = true;
};

const deleteConfirmed = async () => {
  const field = fieldToDelete.value;
  if (!field) return;
  try {
    await deleteCandidateField(field.field_key);
    store.removeDefinition(field.field_key);
  } catch (error) {
    console.error('Failed to delete candidate field:', error);
  } finally {
    fieldToDelete.value = null;
  }
};

/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(async () => {
  // admin-only page; also requires the ec_data_vault feature (candidate data collection)
  if (!userStore.currentUser?.is_admin || !featureStore.featureEcDataVault) {
    router.replace({ name: 'settings' });
    return;
  }
  await store.fetchDefinitions(true);
});
</script>
<style scoped>
.fields-card {
  max-width: 640px;
}

.field-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
  min-height: 60px;
}

.field-name {
  flex: 1;
  font-weight: 600;
  color: #101828;
  min-width: 0;
}

.field-type-badge {
  background: #eaecf0;
  color: #475467;
  font-weight: 500;
}

.field-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.field-delete-btn:not(:disabled):hover {
  color: #d92d20;
  border-color: #fda29b;
}

.field-add {
  margin-top: 16px;
}
</style>
